import React, { useEffect, useMemo, useState, useRef } from 'react';
import styles from '../../../styles/DocumentManagement.module.css';
import buttonStyles from '../../../styles/Buttons.module.css';
import DocumentFieldItem from '../DocumentManagement/DocumentFieldItem';
import { DocumentField } from '../../../types/Document';
import { useDocumentContext } from '../../../contexts/DocumentContext';
import { useManagerContext } from '../../../contexts/ManagerContext';

interface DocumentFieldsListProps {
  fields: DocumentField[];
  // Flag to indicate if this is company mode
  isCompanyMode?: boolean;
}

const DocumentFieldsList: React.FC<DocumentFieldsListProps> = ({ 
  fields,
  isCompanyMode = false
}) => {
  const documentContext = useDocumentContext();
  const managerContext = useManagerContext();
  
  // Use manager context for company mode, document context for user mode
  const {
    userFieldValues,
    fetchFieldValues,
    saveFieldValues,
    loading,
    userReminders,
    removeUserFieldReminders,
    selectedUser,
    fetchUserReminders
  } = isCompanyMode ? {
    // In company mode, get data from manager context
    userFieldValues: managerContext?.companyFieldValues || {},
    fetchFieldValues: managerContext?.fetchCompanyFieldValues || (() => {}),
    saveFieldValues: managerContext?.saveCompanyFieldValues || (() => Promise.resolve()),
    loading: managerContext?.loading || false,
    userReminders: [],
    removeUserFieldReminders: () => Promise.resolve(),
    selectedUser: null, // Company mode doesn't have a selected user
    fetchUserReminders: () => Promise.resolve()
  } : {
    // In user mode, get data from document context
    userFieldValues: documentContext.userFieldValues,
    fetchFieldValues: documentContext.fetchFieldValues,
    saveFieldValues: documentContext.saveFieldValues,
    loading: documentContext.loading,
    userReminders: documentContext.userReminders,
    removeUserFieldReminders: documentContext.removeUserFieldReminders,
    selectedUser: documentContext.selectedUser,
    fetchUserReminders: documentContext.fetchUserReminders
  };
  
  const [localFieldState, setLocalFieldState] = useState<{ [key: string]: any }>({});
  const [stateDirty, setStateDirty] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successFade, setSuccessFade] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  
  // Track the last fetched field IDs to prevent redundant fetches
  const lastFetchedIds = useRef<string>('');
  const isFetching = useRef<boolean>(false);

  // Determine field IDs for fetching
  const fieldIds = useMemo(() => fields.map(f => f.id).filter(Boolean), [fields]);

  // Fetch existing values when fields change
  useEffect(() => {
    const idsKey = fieldIds.sort().join(',');
    
    // Skip if already fetching or if we just fetched these exact IDs
    if (fieldIds.length === 0 || isFetching.current || lastFetchedIds.current === idsKey) {
      return;
    }
    
    isFetching.current = true;
    lastFetchedIds.current = idsKey;
    
    // Fire and forget; context manages state
    Promise.resolve(fetchFieldValues(fieldIds)).finally(() => {
      isFetching.current = false;
    });
  }, [fetchFieldValues, fieldIds]);

  // Keep local state in sync with context when not dirty
  useEffect(() => {
    if (!stateDirty) {
      const next: Record<string, any> = {};
      fieldIds.forEach(id => { next[id] = userFieldValues[id] ?? ''; });
      setLocalFieldState(next);
    }
  }, [userFieldValues, stateDirty, fieldIds]);

  const handleFieldChange = (fieldId: string, value: any): void => {
    setLocalFieldState(prev => ({ ...prev, [fieldId]: value }));
    setStateDirty(true);
  };

  const handleSave = async () => {
    setSaveError(null);
    const payload: Record<string, any> = {};
    fieldIds.forEach(id => { payload[id] = localFieldState[id] ?? ''; });
    const result = await saveFieldValues(payload);
    if (!result.success) {
      setSaveError(result.error || 'Failed to save');
      return;
    }
    // After saving, check for reminders and remove them for fields that were missing and now have a value
    if (userReminders && userReminders.length > 0) {
      for (const fieldId of fieldIds) {
        if (localFieldState[fieldId] !== undefined && localFieldState[fieldId] !== null && localFieldState[fieldId] !== '') {
          if (selectedUser?.id) {
            removeUserFieldReminders(fieldId, selectedUser.id);
          }
        }
      }
    }
    setShowSuccess(true);
    setSuccessFade(false);
    setTimeout(() => setSuccessFade(true), 1200);
    setTimeout(() => setShowSuccess(false), 2000);
    setStateDirty(false);

    if (selectedUser?.id && typeof fetchUserReminders === 'function') {
      await fetchUserReminders(selectedUser.id);
    }
  };

  const handleCancel = () => {
    const next: Record<string, any> = {};
    fieldIds.forEach(id => { next[id] = userFieldValues[id] ?? ''; });
    setLocalFieldState(next);
    setSaveError(null);
    setStateDirty(false);
  };

  if (!fields.length) return null;

  return (
    <div className={styles.fullWidth}>
      {showSuccess && (
        <div className={styles.save_successful_popup + (successFade ? ' ' + styles.fade : '')}>
          Save successful!
        </div>
      )}

      {saveError && (
        <div className={styles.inlineErrorBlock}>{saveError}</div>
      )}

      <div className={styles.twoColGrid}>
        {fields.map(field => (
          <DocumentFieldItem
            key={field.id}
            field={field}
            value={localFieldState[field.id] || ''}
            onChange={(value: any) => handleFieldChange(field.id, value)}
            loading={loading}
            isCompanyMode={isCompanyMode}
          />
        ))}
      </div>

      {stateDirty && (
        <div className={styles.mt24}>
          <button onClick={handleSave} className={buttonStyles.button}>
            Save
          </button>
          <button onClick={handleCancel} className={buttonStyles.button + ' ' + buttonStyles.secondary + ' ' + styles.ml8}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default React.memo(DocumentFieldsList);
